import { Seo } from "../components/site/Seo";
import { PageHero } from "../components/site/PageHero";
import { Reveal, SectionHead, Eyebrow, MaskLines } from "../components/site/Motion";
import { IconCard } from "../components/site/IconCard";
import { CTABand } from "../components/site/CTABand";
import { CTAButton } from "../components/site/CTAButton";
import { IMG, PILLARS, EXPERIENCE_STAGES } from "../content/site";
import { MilestoneStat } from "../components/site/MilestoneStat";
import { ProcessSteps } from "../components/site/ProcessSteps";

const ROLES = [
  { id: "advisory", icon: "ScanSearch", title: "Renewable energy advisory", text: "Helping decision-makers understand solar models, their requirements and their commercial implications in plain language." },
  { id: "procurement-advisory", icon: "Network", title: "Solar procurement advisory", text: "Helping consumers define their solar requirement and approach developers in a structured, well-prepared way." },
  { id: "evaluation", icon: "FileSearch", title: "Techno-commercial evaluation", text: "Assessing feasibility and comparing proposals on assumptions, scope, tariffs, terms and risks." },
  { id: "connecting", icon: "Handshake", title: "Connecting the right partners", text: "Identifying developers and EPC companies suited to each consumer's project, location and preferred model." },
];

const PRINCIPLES = [
  ["Plain language", "Solar models and contracts explained clearly to finance, operations and sustainability teams."],
  ["Case-by-case evaluation", "Every recommendation is based on the specific site, consumption and regulations that apply."],
  ["No guaranteed claims", "We state assumptions openly and do not promise savings, approvals or outcomes."],
];

export default function AboutPage() {
  return (
    <>
      <Seo title="About Us" path="/about" description="Solitaire India provides renewable energy advisory, solar procurement advisory and techno-commercial evaluation for C&I consumers, connecting them with suitable implementation partners." />
      <PageHero
        eyebrow="About Solitaire India"
        title={["Advisory that connects", "energy demand with", <span className="font-serif-i text-[#3f8a0f]">the right partners.</span>]}
        intro="Based in Mumbai, Solitaire India works at the intersection of commercial and industrial energy consumers, solar developers and EPC companies."
        image={IMG.commercial}
        imageAlt="Commercial rooftop solar installation over an urban industrial area"
      />
      <section className="bg-[#F9F8F3] py-24 md:py-32" data-testid="about-role">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <SectionHead eyebrow="Our role" title={["What we do for", "C&I consumers."]} intro="Businesses exploring solar often face a crowded market of models, offers and terms. We help them make sense of it, then help bring the right parties together." />
          <div className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {ROLES.map((r, i) => <IconCard key={r.id} {...r} i={i} prefix="about-role" />)}
          </div>
        </div>
      </section>
      <section className="bg-[#F2EFE7] py-24 md:py-32" data-testid="about-experience">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <SectionHead eyebrow="Our experience" title={["From early development", "to commissioning."]} intro="Our team has supported solar projects across their lifecycle: shaping demand and commercial structure in the early stages, aligning consumers with developers and EPC partners, coordinating approvals and milestones, and staying involved through to commissioning." />
            </div>
            <Reveal className="lg:col-span-5" delay={0.15}>
              <div className="rounded-2xl bg-[#082619] p-8 md:p-10">
                <MilestoneStat />
                <p className="mt-6 text-sm leading-relaxed text-[#B4C7BA]">This experience shapes how we evaluate new opportunities: with a practical understanding of what it takes to move a project from proposal to a commissioned plant.</p>
              </div>
            </Reveal>
          </div>
          <div className="mt-16"><ProcessSteps steps={EXPERIENCE_STAGES} /></div>
        </div>
      </section>
      <section className="relative bg-[#082619] py-24 md:py-32 grain" data-testid="about-philosophy">
        <div className="relative z-10 mx-auto max-w-[1440px] px-5 md:px-10">
          <Reveal><Eyebrow tone="light">Brand philosophy</Eyebrow></Reveal>
          <h2 className="font-display mt-8 text-5xl sm:text-6xl lg:text-8xl font-medium tracking-tighter text-[#F9F8F3]">
            <MaskLines inView lines={[<>People <span className="text-[#74DC26]">|</span> Planet</>, <><span className="text-[#74DC26]">|</span> <span className="font-serif-i">Progress</span></>]} />
          </h2>
          <div className="mt-16 grid gap-10 md:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.word} delay={i * 0.1} className="border-t border-white/15 pt-6">
                <span className="font-mono text-sm text-[#74DC26]">0{i + 1}</span>
                <h3 className="font-display mt-4 text-2xl font-medium text-[#F9F8F3]">{p.word}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#B4C7BA]">{p.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-[#F9F8F3] py-24 md:py-32" data-testid="about-principles">
        <div className="mx-auto grid max-w-[1440px] gap-14 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="How we engage" title={["Principles behind", "every engagement."]} />
            <Reveal className="mt-10"><CTAButton to="/services" variant="forest" testId="about-services-cta">Explore Our Services</CTAButton></Reveal>
          </div>
          <div className="lg:col-span-7">
            {PRINCIPLES.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.08} className="grid gap-3 border-b border-[#E5E1D3] py-8 md:grid-cols-[1fr_1.4fr]">
                <h3 className="font-display text-2xl font-medium text-[#082619]">{t}</h3>
                <p className="text-base leading-relaxed text-[#4C6053]">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CTABand />
    </>
  );
}
